class VrTourPopup {
  constructor(targetElement, dialog, options = {}) {
    this.targetElement = targetElement;
    this.dialog = dialog;
    this.iframe = dialog ? dialog.querySelector('iframe') : null;
    this.dialogCloser = null;
    this.closeTimer = null;

    this.options = {
      enableFocusTrap: options.enableFocusTrap ?? true,
      onOpen: options.onOpen || null,
      onClose: options.onClose || null,
      ...options,
    };
  }

  init() {
    if (!this.dialog) {
      return;
    }

    const dialogContent = this.dialog.querySelector('div');
    this.setupFocusTrap();
    this.openPopup(dialogContent);
    this.closePopup(dialogContent);
  }

  setupFocusTrap() {
    if (!this.options.enableFocusTrap) return;

    const startFocusGuard = this.dialog.querySelector('[data-focus-guard="start"]');
    const endFocusGuard = this.dialog.querySelector('[data-focus-guard="end"]');

    if (startFocusGuard && endFocusGuard) {
      startFocusGuard.addEventListener('focus', () => {
        this.focusLastInteractiveElement();
      });

      endFocusGuard.addEventListener('focus', () => {
        this.focusFirstInteractiveElement();
      });
    }

    this.dialog.addEventListener('keydown', (e) => {
      this.trapFocus(e);
    });

    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.closeDialog();
    });
  }

  openPopup(dialogContent) {
    if (this.targetElement) {
      this.targetElement.addEventListener('click', (e) => {
        e.preventDefault();
        dialogContent.classList.remove('vr-tour__dialog-content--fade-in');
        // eslint-disable-next-line no-shadow
        this.dialog.showModal();
        this.toggleIframe(true);
        if (this.dialogCloser) {
          this.dialogCloser.removeAttribute('tabindex');
        }

        setTimeout(() => {
          document.body.classList.add('dialog-open');
        }, 500);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            dialogContent.classList.add('vr-tour__dialog-content--fade-in');
          });
        });
      });
    }
  }

  closePopup(dialogContent) {
    const closeButton = this.dialog.querySelector('.vr-tour__dialog-close');
    if (!closeButton) return;

    this.dialogCloser = closeButton;
    closeButton.addEventListener('click', () => {
      this.closeDialog(dialogContent);
    });
  }

  closeDialog(dialogContent) {
    if (!this.dialog.open) return;

    if (dialogContent) {
      dialogContent.classList.remove('vr-tour__dialog-content--fade-in');
    }
    document.body.classList.remove('dialog-open');

    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.toggleIframe(false);
      if (this.dialog.open) this.dialog.close();
      this.closeTimer = null;
      if (this.options.onClose) this.options.onClose();
    }, 500);
  }

  getDialogFocusableElements() {
    if (!this.dialog) return [];
    const { iframe } = this;
    const elements = [this.dialogCloser, iframe].filter(Boolean);
    return elements.filter((element) => (
      !(element instanceof HTMLButtonElement && element.disabled)
      && element.tabIndex !== -1
    ));
  }

  focusFirstInteractiveElement() {
    const focusableElements = this.getDialogFocusableElements();
    if (focusableElements.length === 0) return;
    focusableElements[0].focus();
  }

  focusLastInteractiveElement() {
    const focusableElements = this.getDialogFocusableElements();
    if (focusableElements.length === 0) return;
    focusableElements[focusableElements.length - 1].focus();
  }

  clearDialogFocus() {
    if (!this.dialog) return;

    const clearFocus = () => {
      const { activeElement } = document;
      if (!activeElement || !(activeElement instanceof HTMLElement)) return;
      if (!this.dialog.contains(activeElement)) return;
      activeElement.blur();
    };

    clearFocus();
    requestAnimationFrame(clearFocus);
    setTimeout(clearFocus, 0);
  }

  trapFocus(e) {
    if (e.key !== 'Tab') return;

    const focusableElements = this.getDialogFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const { activeElement } = document;

    if (e.shiftKey && activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
      return;
    }

    if (!e.shiftKey && activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }

  toggleIframe(show) {
    if (this.iframe) {
      if (show) {
        this.iframe.src = this.iframe.dataset.vrUrl;
      } else {
        this.iframe.src = '';
      }
    }
  }
}

export { VrTourPopup };

export default function VrTourPopupInit(targetElement, dialog) {
  const vrTourPopup = new VrTourPopup(targetElement, dialog);
  vrTourPopup.init();
}
